import { z } from 'zod';

/**
 * @aiPurpose canonical wire shape for an object-storage key. Single
 *   source of truth so request photo references, attachments, and
 *   presigned-URL responses agree on what counts as a valid key.
 * @aiInvariant non-empty, non-whitespace, NOT a URL. The storage
 *   capsule key-prefix grammar (`request-item-photo/...`,
 *   `request-attachment/...`) is enforced downstream by the backend's
 *   signing logic; the wire layer rejects the obvious credential-leak
 *   shape — an attacker submitting a presigned URL as a photo or
 *   attachment value would otherwise have it stored under a
 *   `photos`/`attachments` field that web `REDACT_KEYS` does not mask.
 * @aiSecurity rejecting `://` blocks an attacker from smuggling a
 *   bearer-credential URL into a durable photo/attachment field.
 */
export const StorageObjectKeySchema = z
  .string()
  .min(1)
  .refine((s) => s.trim().length > 0, 'object key must not be whitespace-only')
  .refine((s) => !s.includes('://'), 'object key must not be a URL');
export type StorageObjectKey = z.infer<typeof StorageObjectKeySchema>;

/**
 * @aiPurpose canonical issued-key shape — `<scope>/<userUuid>/<objectUuid>`
 *   where each segment is anchored, the scope is a `StorageUploadScopeSchema`
 *   value, and both UUIDs are RFC 4122. This is the EXACT shape the
 *   backend's `StorageService.assertOwnsKey` will admit; the broader
 *   `StorageObjectKeySchema` admits stored references that may carry
 *   forward-compat shapes (e.g. once `#314` binds keys to RequestItem
 *   rows). Wire endpoints that hand a key to the download path use
 *   `IssuedStorageObjectKeySchema` so a client can never construct a
 *   request that passes Zod but always 403s at the controller.
 * @aiInvariant this pattern is the ONLY issued-key grammar. The backend's
 *   `StorageService.parseIssuedKey` validates against
 *   `IssuedStorageObjectKeySchema` itself rather than keeping a copy, so
 *   there is no second form to drift from; adding a scope here without the
 *   matching backend authz update is a contract bug.
 * @aiSecurity also blocks protocol-relative URLs (e.g.
 *   `//host/path?sig=...`) that the broad `://` filter on
 *   `StorageObjectKeySchema` does not catch — relevant only on the
 *   download-request envelope where credential-leak smuggling matters.
 */
export const ISSUED_OBJECT_KEY_PATTERN =
  /^(?:request-item-photo|request-attachment|carrier-price-list|lead-file|commercial-proposal-photo|logistics-calculation-photo|cargo-import-file|cargo-payment-doc|cargo-problem-attachment|cargo-problem-video)\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const IssuedStorageObjectKeySchema = StorageObjectKeySchema.refine(
  (s) => ISSUED_OBJECT_KEY_PATTERN.test(s),
  'object key must be of form <scope>/<userUuid>/<objectUuid>',
);
export type IssuedStorageObjectKey = z.infer<typeof IssuedStorageObjectKeySchema>;

/**
 * @aiPurpose the closed set of presigned-upload scopes. Named (not inline) so a
 *   feature can bind its own wire field to exactly one of them.
 * @aiSecurity adding a value requires a backend authz update — the scope
 *   decides the bucket key prefix an issued URL is bound to.
 */
export const StorageUploadScopeSchema = z.enum([
  'request-item-photo',
  'request-attachment',
  'carrier-price-list',
  'lead-file',
  'commercial-proposal-photo',
  'logistics-calculation-photo',
  // #1227: Cargo registry scopes. `cargo-import-file` is the Yan/full-truck
  // XLSX upload (50 MiB, Excel MIME only); `cargo-payment-doc` is a client
  // payment proof document (25 MiB, lead-file document MIME allowlist).
  'cargo-import-file',
  'cargo-payment-doc',
  'cargo-problem-attachment',
  'cargo-problem-video',
]);
export type StorageUploadScope = z.infer<typeof StorageUploadScopeSchema>;

/**
 * @aiPurpose issued-key schema narrowed to ONE scope, for wire fields that
 *   accept objects from a single upload flow.
 * @aiSecurity `IssuedStorageObjectKeySchema` admits every issued scope, so a
 *   feature field using it accepts a key minted for an unrelated flow — the
 *   caller's own `lead-file` or `request-attachment` object, which the backend
 *   ownership check would still pass. This narrows the wire gate to the scope
 *   the endpoint actually serves; the backend scope/ownership check stays in
 *   place as the second layer.
 */
export function issuedStorageObjectKeyForScope(
  scope: StorageUploadScope,
): z.ZodType<string, z.ZodTypeDef, string> {
  return IssuedStorageObjectKeySchema.refine(
    (key) => key.startsWith(`${scope}/`),
    `object key must be issued for the ${scope} scope`,
  );
}

/** Exact browser video types accepted by the cargo-problem evidence flow. */
export const CARGO_PROBLEM_VIDEO_ALLOWED_MIME = [
  'video/mp4',
  'video/quicktime',
  'video/webm',
] as const;
export const CargoProblemVideoMimeSchema = z.enum(CARGO_PROBLEM_VIDEO_ALLOWED_MIME);
export type CargoProblemVideoMime = z.infer<typeof CargoProblemVideoMimeSchema>;
export const CargoProblemVideoObjectKeySchema =
  issuedStorageObjectKeyForScope('cargo-problem-video');

/**
 * @aiPurpose request envelope for the backend's "give me a presigned
 *   upload URL" endpoint. The client describes what it intends to upload
 *   (mime type, byte length, scope); the server returns an upload URL
 *   bound to those constraints.
 * @aiInvariant `.strict()` rejects unknown keys so a typo in the client
 *   payload (e.g. `contentLen` instead of `contentLength`) surfaces as
 *   400 VALIDATION_ERROR instead of being silently ignored, which would
 *   produce a presigned URL with no Content-Length policy.
 * @aiSecurity `scope` constrains the bucket key prefix the URL is bound
 *   to; the backend uses it to scope authz on the issued URL. Adding
 *   new scope values requires a backend authz update.
 */
export const StorageUploadUrlRequestSchema = z
  .object({
    contentType: z.string().min(1),
    contentLength: z.number().int().positive(),
    scope: StorageUploadScopeSchema,
  })
  .strict();
export type StorageUploadUrlRequest = z.infer<typeof StorageUploadUrlRequestSchema>;

/**
 * @aiPurpose response envelope for a presigned upload URL. The client
 *   PUTs to `uploadUrl`, then references the object by `objectKey`.
 * @aiInvariant `.strict()` keeps the envelope in lockstep with the
 *   client schema so backend additions land as a coordinated wire
 *   change, not as an unannounced extension.
 */
export const StorageUploadUrlResponseSchema = z
  .object({
    uploadUrl: z.string().url(),
    objectKey: StorageObjectKeySchema,
    expiresAt: z.string().datetime(),
  })
  .strict();
export type StorageUploadUrlResponse = z.infer<typeof StorageUploadUrlResponseSchema>;

/**
 * @aiPurpose request envelope for "give me a presigned download URL for
 *   this object key". The endpoint is POST + body (not GET + path
 *   segment) because object keys contain `/` separators, which edge
 *   proxies have historically rejected even when percent-encoded as
 *   `%2F`.
 * @aiInvariant `.strict()` rejects unknown keys so a typo on the client
 *   side surfaces as 400 VALIDATION_ERROR instead of being silently
 *   dropped. `objectKey` uses `IssuedStorageObjectKeySchema` so wire
 *   validation matches what the backend will actually accept — a key in
 *   any other shape would 403 at the controller and is rejected at the
 *   wire layer instead.
 */
export const StorageDownloadUrlRequestSchema = z
  .object({ objectKey: IssuedStorageObjectKeySchema })
  .strict();
export type StorageDownloadUrlRequest = z.infer<typeof StorageDownloadUrlRequestSchema>;

/**
 * @aiPurpose response envelope for a presigned download URL bound to a
 *   specific object key. Clients GET `downloadUrl` directly; the URL
 *   expires at `expiresAt`.
 * @aiInvariant `.strict()` keeps client and server in lockstep on the
 *   shape; see the upload-response invariant.
 */
export const StorageDownloadUrlResponseSchema = z
  .object({
    downloadUrl: z.string().url(),
    expiresAt: z.string().datetime(),
  })
  .strict();
export type StorageDownloadUrlResponse = z.infer<typeof StorageDownloadUrlResponseSchema>;
